import React, { useEffect, useState } from "react";
import { Building2, ShieldCheck, Mail, Landmark, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { company } from "../mock";
import api, { errMsg } from "../api";

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`} {...rest}>{children}</div>;
}

function Row({ Icon, label, value, ok }) {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100"><Icon className="h-4 w-4 text-violet-600" /></div>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-sm font-medium text-slate-800">{value}</div>
      </div>
      {ok !== undefined && (ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />)}
    </div>
  );
}

function StatusCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/system/status").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Card>Loading integration status…</Card>;
  return (
    <Card data-testid="integration-status">
      <h2 className="text-base font-bold text-slate-900">Integration status</h2>
      <p className="text-sm text-slate-500">Live configuration checks — nothing here is simulated.</p>
      <div className="mt-2 divide-y divide-slate-100">
        <Row Icon={Building2} label="Organization" value={company.legal} />
        <Row Icon={ShieldCheck} label="Supabase (Auth, profiles, Storage)" value={s.supabase.configured ? "Connected" : "Not configured"} ok={s.supabase.configured} />
        <Row Icon={Mail} label="Transactional email" value={`${s.email.detail}${s.email.sender ? ` Sender: ${s.email.sender}` : ""}${s.email.error ? ` · Last error: ${s.email.error}` : ""}`} ok={s.email.configured && !s.email.error} />
        <Row Icon={Mail} label="Equipment team email" value={s.equipment_notify_email || "Not set — Applicant Data → Equipment"} ok={!!s.equipment_notify_email} />
        <Row Icon={Mail} label="Support inbox" value={s.support_email || "Not set — add it below"} ok={!!s.support_email} />
        <Row Icon={Landmark} label="Patriot Software API" value={s.patriot_api.detail} ok={s.patriot_api.configured} />
      </div>
    </Card>
  );
}

function SupportConfigCard() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState({ email: "", phone: "", hours: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/support/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/support/config", cfg); setCfg(r.data); toast({ title: "Support contact saved" }); }
    catch (e) { toast({ title: "Save failed", description: errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };
  return (
    <Card data-testid="support-config-card">
      <h2 className="text-base font-bold text-slate-900">Support contact</h2>
      <p className="text-sm text-slate-500">Shown on the Support page and login help. Support requests are emailed to this address.</p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5"><Label>Support email</Label><Input value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} data-testid="support-config-email" /></div>
        <div className="space-y-1.5"><Label>Phone</Label><Input value={cfg.phone} onChange={(e) => setCfg({ ...cfg, phone: e.target.value })} data-testid="support-config-phone" /></div>
        <div className="space-y-1.5"><Label>Hours</Label><Input value={cfg.hours} onChange={(e) => setCfg({ ...cfg, hours: e.target.value })} placeholder="Mon–Fri, 9am–5pm ET" data-testid="support-config-hours" /></div>
      </div>
      <Button onClick={save} disabled={busy} className="btn-primary-gradient mt-4 border-0 text-white" data-testid="support-config-save">{busy ? "Saving…" : "Save"}</Button>
    </Card>
  );
}

function SupportQueue() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [reply, setReply] = useState({});
  const load = () => api.get("/admin/support/requests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (r, status) => {
    try { await api.put(`/admin/support/requests/${r.id}`, { status, reply: reply[r.id] || "" }); toast({ title: `Marked ${status}` }); load(); }
    catch (e) { toast({ title: "Update failed", description: errMsg(e), variant: "destructive" }); }
  };
  return (
    <Card data-testid="support-queue">
      <h2 className="text-base font-bold text-slate-900">Support requests</h2>
      <div className="mt-3 space-y-3">
        {rows.length === 0 && <div className="text-sm text-slate-400">No support requests.</div>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-100 p-3" data-testid={`support-req-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><div className="text-sm font-semibold text-slate-800">{r.subject}</div><div className="text-xs text-slate-400">{r.name} · {r.email} · {new Date(r.created_at).toLocaleString()}</div></div>
              <Select value={r.status} onValueChange={(v) => update(r, v)}>
                <SelectTrigger className="w-36" data-testid={`support-status-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{["Open", "In Progress", "Resolved"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {r.details && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{r.details}</p>}
            {r.reply && <div className="mt-2 text-xs text-violet-700">Reply sent: {r.reply}</div>}
            <Textarea rows={2} className="mt-2" placeholder="Reply to the applicant (sent with the status change)" value={reply[r.id] || ""} onChange={(e) => setReply({ ...reply, [r.id]: e.target.value })} data-testid={`support-reply-${r.id}`} />
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function SystemSettings() {
  return (
    <div className="space-y-6">
      <StatusCard />
      <SupportConfigCard />
      <SupportQueue />
    </div>
  );
}
